import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { NavigateFunction } from 'react-router-dom'

import { OktaAuth } from '@okta/okta-auth-js'
import queryString from 'query-string'
import Cookies from 'universal-cookie'

import { UNAUTHENTICATED_REDIRECT_URL } from '@lyrahealth-inc/lyraweb-mobile/src/hooks/useShareSessionStorageAcrossTabs'
import { INVALID, LOCAL_STORAGE_KEY } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import {
  COMMON_MIXPANEL_PAGE,
  COMMON_MIXPANEL_PROPERTIES,
  LOGIN_EVENTS,
  REGISTRATION_EVENTS,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import {
  PROVIDER_RESULTS_SURVEY_DATE_LAST_SEEN_KEY,
  SHOW_PROVIDER_RESULTS_SURVEY_KEY,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/pendoConstants'
import {
  APP_ROUTE,
  PAGE_ROUTES,
  REDIRECT_URL_KEY,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/routingConstants'
import {
  IDENTITY_PROVIDERS,
  REGISTRATION_QUERY_PARAMS,
} from '@lyrahealth-inc/shared-app-logic/src/features/registration/constants'
import { DirectLinkSource } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'
import { getShowProviderResultsSurveyUsingDateLastSeen } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/utils'
import { LoginResponse, type PendingSsoIdentity } from '@lyrahealth-inc/shared-app-logic/src/models/login/Login'

import mixpanel from '../../data/mixpanel/mixpanel-browser'
import { makeOktaAuthOptions } from '../../features/login/loginUtils'
import {
  ATTESTATION_VERIFICATION,
  CONNECTED_SERVICE_ATTESTATION_QUERY_PARAMS,
  CONNECTED_SERVICE_DATA_COOKIE,
  VERIFY_ATTESTATION_QUERY_PARAMS,
} from '../constants/appConstants'
import { GENERIC, REGISTERED } from '../constants/errorConstants'
import { BASIC_INFO_FORM_PATH } from '../constants/registrationFormConstants'
import { addFromUrlRedirectParam, addStickyQueryParams } from '../utils/routingUtils'
import { setPendingSsoInterstitialCookie, setSsoRegistrationDataCookie } from '../utils/ssoRegistrationUtils'
import { logToSentry } from '../utils/userUtils'

const CARE_TOOL_CN_LOGIN_PATH = '/cn-login'
const HOMEBASE_NAVIGATION_TIMEOUT_MS = 5_000

function isCareToolCnLoginPath(path: string): boolean {
  try {
    const parsedUrl = new URL(path, window.location.origin)
    return (
      parsedUrl.origin === window.location.origin &&
      (parsedUrl.pathname === CARE_TOOL_CN_LOGIN_PATH || parsedUrl.pathname.startsWith(`${CARE_TOOL_CN_LOGIN_PATH}/`))
    )
  } catch {
    return false
  }
}

export type UseAuthenticateProps = {
  login: Function // eslint-disable-line @typescript-eslint/no-unsafe-function-type
  trackEventWithObj: Function // eslint-disable-line @typescript-eslint/no-unsafe-function-type
  markUserAsStale: Function // eslint-disable-line @typescript-eslint/no-unsafe-function-type
  mixpanelInitialized: boolean
  getOktaSession?: () => Promise<{ idp?: { id: string; type: string } }>
  navigate: NavigateFunction
  oktaClientId: string
  oktaBaseUrl: string
  oktaAppleSSOClientId: string
  oktaGoogleSSOClientId: string
  setSsoIdentityProvider: Function // eslint-disable-line @typescript-eslint/no-unsafe-function-type
  setSsoToken: Function // eslint-disable-line @typescript-eslint/no-unsafe-function-type
  setPendingSsoIdentity: (value: PendingSsoIdentity) => void
  userId?: string
  customerId?: string
  is1LyraEnabledForHomebase?: boolean
  isHomebaseFlagIdentified: boolean
  isUserTeen?: boolean
  directLinkSource?: DirectLinkSource
  /** Clears the persisted direct-link source once a Manager Hub redirect has been consumed for a teen. */
  clearDirectLinkSource?: () => void
  /** True once redux-persist has rehydrated session state such as the onboard direct-link source. */
  isStoreHydrated?: boolean
}
const useAuthenticate = ({
  login,
  trackEventWithObj,
  markUserAsStale,
  mixpanelInitialized,
  getOktaSession,
  navigate,
  oktaClientId,
  oktaBaseUrl,
  oktaAppleSSOClientId,
  oktaGoogleSSOClientId,
  setSsoIdentityProvider,
  setSsoToken,
  setPendingSsoIdentity,
  userId,
  customerId,
  is1LyraEnabledForHomebase,
  isHomebaseFlagIdentified,
  isUserTeen,
  directLinkSource,
  clearDirectLinkSource,
  isStoreHydrated,
}: UseAuthenticateProps) => {
  const { code, state, error, error_description, fromLogin } = queryString.parse(window.location.search) || {}

  // For SSO with partner identity providers:
  // Okta sets a `fromLogin` query parameter in the redirect after authenticating a user from a federated identity provider
  const isLoginFromPartnerSSO = fromLogin === 'true'

  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(
    (Boolean(code) && Boolean(state)) || isLoginFromPartnerSSO,
  )
  const [loginEvent, setLoginEvent] = useState<LOGIN_EVENTS>()
  const [loginEventProps, setLoginEventProps] = useState({})
  const [hasTrackedLogin, setHasTrackedLogin] = useState(false)
  const [hasPendingHomebaseNavigation, setHasPendingHomebaseNavigation] = useState(false)
  // A stored redirect waits for the persisted store to rehydrate so the direct-link source is known; only a
  // Manager Hub redirect then also waits for the current user, since teens from the Hub go to Homebase.
  const [pendingRedirectPath, setPendingRedirectPath] = useState<string | null>(null)
  const hasStartedLogin = useRef(false)
  const doLogin = useCallback(async () => {
    try {
      const loginResponse: LoginResponse = await login({
        code,
        redirect_uri: window.location.origin + '/login/okta',
        state,
      })
      markUserAsStale()
      if (getOktaSession) {
        getOktaSession()
          .then((session) => {
            if (session.idp?.type === 'SOCIAL') {
              if (session.idp?.id === oktaAppleSSOClientId) {
                setLoginEvent(LOGIN_EVENTS.LOGIN_APPLE_SSO)
              } else if (session.idp?.id === oktaGoogleSSOClientId) {
                setLoginEvent(LOGIN_EVENTS.LOGIN_GOOGLE_SSO)
              }
            } else if (session.idp?.type === 'FEDERATION') {
              setLoginEvent(LOGIN_EVENTS.LOGIN_PARTNER_SSO)
              if (loginResponse.partnerIdp?.idpId === session.idp?.id) {
                setLoginEventProps({
                  [COMMON_MIXPANEL_PROPERTIES.PARTNER_NAME]: loginResponse.partnerIdp.shortName,
                  ...(loginResponse.entryPoint && {
                    [COMMON_MIXPANEL_PROPERTIES.SSO_ENTRY_POINT]: loginResponse.entryPoint,
                  }),
                })
              } else {
                console.error('Unexpected: Partner IDP mismatch between Okta session and login response!')
              }
            } else {
              setLoginEvent(LOGIN_EVENTS.LOGIN)
            }
          })
          .catch((error) => {
            console.warn('Failed to get Okta session', error)
            setLoginEvent(LOGIN_EVENTS.LOGIN)
            if (loginResponse.ssoUserInfo && loginResponse.partnerIdp) {
              // This is the case where an unregistered user came from partner sso.
              // If the user completes registration and logs in, we want to attribute it as a successful partner sso login in mixpanel metrics.
              setLoginEvent(LOGIN_EVENTS.LOGIN_PARTNER_SSO)
              setLoginEventProps({
                [COMMON_MIXPANEL_PROPERTIES.PARTNER_NAME]: loginResponse.partnerIdp.shortName,
                ...(loginResponse.entryPoint && {
                  [COMMON_MIXPANEL_PROPERTIES.SSO_ENTRY_POINT]: loginResponse.entryPoint,
                }),
              })
            }
          })
      } else {
        setLoginEvent(LOGIN_EVENTS.LOGIN)
      }
      const needsMicrositeRedirect = Boolean(loginResponse.loginRedirect?.redirectUrl)
      const ssoRedirectPath = loginResponse.loginRedirect?.redirectPath

      if (loginResponse.ssoUserInfo) {
        if (loginResponse.ssoUserInfo.isAccountReconciliation) {
          trackEventWithObj({
            event: REGISTRATION_EVENTS.ACCOUNT_RECONCILIATION_LOGIN,
            options: { transport: 'sendBeacon' },
          })
        }
        // SSO INTERSTITIAL: First-time SSO users choose between creating a new account and linking an existing one.
        // The backend only sends ssoInterstitialKey for users who need the interstitial.
        if (loginResponse.ssoUserInfo.ssoInterstitialKey) {
          const pendingIdentity: PendingSsoIdentity = {
            email: loginResponse.ssoUserInfo.email,
            givenName: loginResponse.ssoUserInfo.givenName,
            familyName: loginResponse.ssoUserInfo.familyName,
            dob: loginResponse.ssoUserInfo.dob,
            ssoRegistrationVerificationKey: loginResponse.ssoUserInfo.ssoRegistrationVerificationKey,
            ssoInterstitialKey: loginResponse.ssoUserInfo.ssoInterstitialKey,
            ssoRedirectPath: loginResponse.loginRedirect?.redirectPath,
            partnerIdpShortName: loginResponse.partnerIdp?.shortName,
            externalUserId: loginResponse.connectedServiceLoginData?.externalId,
            connectedServiceType: loginResponse.connectedServiceLoginData?.service,
            isAccountReconciliation: loginResponse.ssoUserInfo.isAccountReconciliation,
          }
          if (needsMicrositeRedirect && loginResponse.loginRedirect?.redirectUrl) {
            setPendingSsoInterstitialCookie(pendingIdentity)
            const interstitialURL = new URL(
              PAGE_ROUTES.REGISTER_SSO_INTERSTITIAL,
              loginResponse.loginRedirect.redirectUrl,
            )
            window.location.replace(interstitialURL.toString())
          } else {
            setPendingSsoIdentity(pendingIdentity)
            navigate(PAGE_ROUTES.REGISTER_SSO_INTERSTITIAL)
          }
        } else if (loginResponse.ssoUserInfo.isAccountReconciliation) {
          // SSO RECONCILIATION: Existing user is taken to verification page to receive verification email
          const reconciliationPath = '/register/verify'
          const reconciliationParamsString = queryString.stringify({
            isSSOReconciliation: 'true',
            ssoDataKey: loginResponse.ssoUserInfo.ssoReconciliationDataKey,
          })
          if (needsMicrositeRedirect) {
            const reconciliationURL = new URL(reconciliationPath, loginResponse.loginRedirect!.redirectUrl!)
            reconciliationURL.search = reconciliationParamsString
            window.location.replace(reconciliationURL.toString())
          } else {
            navigate({
              pathname: reconciliationPath,
              search: `?${reconciliationParamsString}`,
            })
          }
        } else if (loginResponse.ssoUserInfo.isUnidentifiableUser && needsMicrositeRedirect) {
          // UNIDENTIFIABLE: User is coming from partner SSO but an email wasn't provided by the Partner IDP, making user unidentifiable with
          // no email available for registration. Currently, this only happens for Elevance which always needs a redirect so we only handle the needsRedirect case.
          window.location.replace(loginResponse.loginRedirect!.redirectUrl!)
        } else {
          // UNREGISTERED: User is coming from partner SSO and needs to be taken to registration page
          const registrationParams = {
            [REGISTRATION_QUERY_PARAMS.ID]: loginResponse.ssoUserInfo.email,
            [REGISTRATION_QUERY_PARAMS.IS_SSO]: 'true',
            ...(loginResponse.ssoUserInfo.ssoRegistrationVerificationKey && {
              [REGISTRATION_QUERY_PARAMS.PARTNER_SSO_REGISTRATION_VERIFICATION_KEY_QUERY_PARAM]:
                loginResponse.ssoUserInfo.ssoRegistrationVerificationKey,
            }),
            ...(ssoRedirectPath && { [REGISTRATION_QUERY_PARAMS.REDIRECT]: ssoRedirectPath }),
            ...(loginResponse.entryPoint && {
              [REGISTRATION_QUERY_PARAMS.SSO_ENTRY_POINT]: loginResponse.entryPoint,
            }),
          }
          if (needsMicrositeRedirect) {
            if (loginResponse.partnerIdp?.shortName) {
              registrationParams[REGISTRATION_QUERY_PARAMS.PARTNER_SSO_REGISTRATION_PARTNER_IDP_QUERY_PARAM] =
                loginResponse.partnerIdp.shortName
            }
            setSsoRegistrationDataCookie({
              email: loginResponse.ssoUserInfo.email,
              givenName: loginResponse.ssoUserInfo.givenName,
              familyName: loginResponse.ssoUserInfo.familyName,
              dob: loginResponse.ssoUserInfo.dob,
              externalUserId: loginResponse.connectedServiceLoginData?.externalId,
              connectedServiceType: loginResponse.connectedServiceLoginData?.service,
            })
            const registrationURL = new URL(BASIC_INFO_FORM_PATH, loginResponse.loginRedirect!.redirectUrl!)
            registrationURL.search = queryString.stringify(registrationParams)
            window.location.replace(registrationURL.toString())
          } else {
            setSsoIdentityProvider(IDENTITY_PROVIDERS.PARTNER)
            setSsoToken('')
            navigate(
              {
                pathname: BASIC_INFO_FORM_PATH,
                search: `?${queryString.stringify(registrationParams)}`,
              },
              { state: loginResponse },
            )
          }
        }
      } else if (loginResponse.eligibleCustomerForTransferAccount && customerId) {
        window.localStorage.setItem(
          LOCAL_STORAGE_KEY.TRANSFER_ACCOUNT,
          loginResponse.eligibleCustomerForTransferAccount,
        )
        navigate(APP_ROUTE.TRANSFER_ACCOUNT_CONFIRMATION)
      } else if (
        loginResponse.connectedServiceLoginData &&
        loginResponse.connectedServiceLoginData.service &&
        loginResponse.connectedServiceLoginData.needsAttestation
      ) {
        const cookies = new Cookies()
        cookies.remove(CONNECTED_SERVICE_DATA_COOKIE)
        const connectedServiceAttestationParams = {
          [CONNECTED_SERVICE_ATTESTATION_QUERY_PARAMS.CONNECTED_SERVICE]:
            loginResponse.connectedServiceLoginData.service,
        }
        navigate({
          pathname: PAGE_ROUTES.CONNECTED_SERVICE_ATTESTATION,
          search: `?${queryString.stringify(connectedServiceAttestationParams)}`,
        })
      } else if (loginResponse.needsAttestation) {
        // ATTESTATION: Registered user needs to verify their attestation
        const attestationParams = {
          [VERIFY_ATTESTATION_QUERY_PARAMS.TOKEN]: loginResponse.needsAttestation.token,
          [VERIFY_ATTESTATION_QUERY_PARAMS.ID]: loginResponse.needsAttestation.id,
          [VERIFY_ATTESTATION_QUERY_PARAMS.SHOW_ELIGIBILITY]: loginResponse.needsAttestation.showEligibility,
          ...(ssoRedirectPath && { [VERIFY_ATTESTATION_QUERY_PARAMS.REDIRECT]: ssoRedirectPath }),
        }

        const attestationSearch = queryString.stringify(attestationParams)
        if (needsMicrositeRedirect) {
          const loginURL = new URL(loginResponse.loginRedirect!.redirectUrl!)
          // In this case redirectUrl is ane time token sign in URL where a redirect param can be specified for post login redirect.
          loginURL.searchParams.set('redirect', `${PAGE_ROUTES.VERIFY_ATTESTATION}?${attestationSearch}`)
          window.location.replace(loginURL.toString())
        } else {
          navigate({
            pathname: PAGE_ROUTES.VERIFY_ATTESTATION,
            search: `?${attestationSearch}`,
          })
        }
      } else {
        // REGISTERED: Registered user needs to be logged in
        // This is the page user was trying to see but had to log in for.
        let attemptedPath = window.sessionStorage.getItem(UNAUTHENTICATED_REDIRECT_URL)
        if (attemptedPath) {
          attemptedPath = addFromUrlRedirectParam(attemptedPath)
        }
        const loginRedirectPath = ssoRedirectPath ?? attemptedPath
        if (needsMicrositeRedirect) {
          // Build the redirect path that the user will land on after the one-time token login.
          // If this is a partner SSO login, append tracking params so the recovery useEffect
          // on the microsite can detect the partner login and fire LOGIN_PARTNER_SSO.
          let redirectPath = loginRedirectPath ?? PAGE_ROUTES.SECURE_INDEX
          if (loginResponse.partnerIdp) {
            redirectPath = queryString.stringifyUrl({
              url: redirectPath,
              query: {
                [REGISTRATION_QUERY_PARAMS.PARTNER_SSO_REGISTRATION_PARTNER_IDP_QUERY_PARAM]:
                  loginResponse.partnerIdp.shortName,
                ...(loginResponse.entryPoint && {
                  [REGISTRATION_QUERY_PARAMS.SSO_ENTRY_POINT]: loginResponse.entryPoint,
                }),
              },
            })
          }
          const loginURL = new URL(loginResponse.loginRedirect!.redirectUrl!)
          // In this case redirectUrl is a one time token sign in URL where a redirect param can be specified for post login redirect.
          loginURL.searchParams.set('redirect', redirectPath)
          window.location.replace(loginURL.toString())
        } else {
          window.localStorage.removeItem(LOCAL_STORAGE_KEY.TRANSFER_ACCOUNT)
          window.localStorage.removeItem(ATTESTATION_VERIFICATION)
          if (loginRedirectPath) {
            window.sessionStorage.removeItem(UNAUTHENTICATED_REDIRECT_URL)
            if (isCareToolCnLoginPath(loginRedirectPath)) {
              window.location.replace(loginRedirectPath)
            } else if (!ssoRedirectPath && attemptedPath) {
              setPendingRedirectPath(loginRedirectPath)
              return
            } else {
              navigate(loginRedirectPath, { replace: true })
            }
          } else {
            if (window.sessionStorage.getItem(REDIRECT_URL_KEY)) {
              navigate(PAGE_ROUTES.SECURE_INDEX)
            } else {
              setHasPendingHomebaseNavigation(true)
              return
            }
          }
        }
      }
      setIsAuthenticating(false)
    } catch {
      setIsAuthenticating(false)
      navigate('/login')
    }
  }, [
    login,
    code,
    state,
    markUserAsStale,
    getOktaSession,
    customerId,
    oktaAppleSSOClientId,
    oktaGoogleSSOClientId,
    navigate,
    setSsoIdentityProvider,
    setSsoToken,
    setPendingSsoIdentity,
  ])

  useEffect(() => {
    if (!pendingRedirectPath) {
      return
    }

    const isManagerHubRedirect = directLinkSource === DirectLinkSource.MANAGER_HUB
    // Other redirects navigate as soon as the store has rehydrated; Manager Hub also needs the member's age.
    if (isStoreHydrated && (!isManagerHubRedirect || userId)) {
      // Teens cannot access Manager Hub, so a destination preserved from the logged-out Hub sends
      // them to Homebase instead. The source is cleared so it cannot reroute a later, unrelated login.
      const shouldRedirectToHomebase = isManagerHubRedirect && isUserTeen
      if (shouldRedirectToHomebase) {
        logToSentry('ManagerHub', {
          userId,
          message: 'Manager Hub redirect replaced with Homebase for a teen after auth',
          type: 'info',
          pendingRedirectPath,
        })
        clearDirectLinkSource?.()
      }
      setPendingRedirectPath(null)
      navigate(shouldRedirectToHomebase ? PAGE_ROUTES.SECURE_INDEX : pendingRedirectPath, { replace: true })
      setIsAuthenticating(false)
      return
    }

    // Do not hold a successful login on the loader if rehydration or the current-user fetch never resolves.
    // A Manager Hub redirect falls back to Homebase because access is still unknown.
    const timeoutId = window.setTimeout(() => {
      setPendingRedirectPath(null)
      navigate(isManagerHubRedirect ? PAGE_ROUTES.SECURE_INDEX : pendingRedirectPath, { replace: true })
      setIsAuthenticating(false)
    }, HOMEBASE_NAVIGATION_TIMEOUT_MS)

    return () => window.clearTimeout(timeoutId)
  }, [clearDirectLinkSource, directLinkSource, isStoreHydrated, isUserTeen, navigate, pendingRedirectPath, userId])

  useEffect(() => {
    const confirmationEmailErrors = [REGISTERED, INVALID, GENERIC]
    const queryParams = queryString.stringify({ error, error_description })
    if (mixpanelInitialized) {
      if (error) {
        trackEventWithObj({
          event: LOGIN_EVENTS.LOGIN_FAILED,
        })
      }
      if (error && typeof error === 'string' && !confirmationEmailErrors.includes(error)) {
        navigate(`/login?${queryParams}`)
      } else if (error && typeof error === 'string' && error === REGISTERED) {
        navigate(`/register/verify?${queryParams}`)
      }
    }
  }, [error, error_description, navigate, trackEventWithObj, mixpanelInitialized])

  const oktaAuth = useMemo(() => {
    if (oktaBaseUrl && oktaClientId) {
      return new OktaAuth(makeOktaAuthOptions(oktaClientId, oktaBaseUrl, window.location.origin))
    }
    return undefined
  }, [oktaBaseUrl, oktaClientId])

  useEffect(() => {
    if (hasStartedLogin.current) {
      return
    }

    if (isLoginFromPartnerSSO && oktaAuth) {
      hasStartedLogin.current = true
      // The user should have a session with Okta after authenticating with the federated identity provider
      // This starts the OIDC authentication code flow to obtain tokens and log in with LyraWeb
      oktaAuth.token.getWithRedirect()
    } else if (isAuthenticating && mixpanelInitialized) {
      hasStartedLogin.current = true
      doLogin()
    }
  }, [doLogin, mixpanelInitialized, isAuthenticating, oktaBaseUrl, oktaClientId, isLoginFromPartnerSSO, oktaAuth])

  // Call mixpanel track, set alias and register super props
  useEffect(() => {
    const localStoragePartnerIdp = window.localStorage.getItem(
      REGISTRATION_QUERY_PARAMS.PARTNER_SSO_REGISTRATION_PARTNER_IDP_QUERY_PARAM,
    )
    const localStorageSsoEntryPoint = window.localStorage.getItem(REGISTRATION_QUERY_PARAMS.SSO_ENTRY_POINT)

    // Also check URL search params for partner SSO tracking data.
    // For registered users redirected to a microsite via one-time token login, the tracking
    // params are passed as query params on the redirect path (since localStorage is per-origin
    // and can't be shared across subdomains). For unregistered users, useRegisterUser writes
    // the params to localStorage on the microsite origin instead.
    const urlSearchParams = new URLSearchParams(window.location.search)
    const urlPartnerIdp = urlSearchParams.get(
      REGISTRATION_QUERY_PARAMS.PARTNER_SSO_REGISTRATION_PARTNER_IDP_QUERY_PARAM,
    )
    const urlSsoEntryPoint = urlSearchParams.get(REGISTRATION_QUERY_PARAMS.SSO_ENTRY_POINT)

    const partnerIdpValue = localStoragePartnerIdp || urlPartnerIdp
    const ssoEntryPointValue = localStorageSsoEntryPoint || urlSsoEntryPoint

    // If partner IDP name was found (from localStorage or URL params), it's because we redirected
    // and the initially set login event was lost. Set the login event to LOGIN_PARTNER_SSO.
    if (!hasTrackedLogin && !!userId && !!customerId && partnerIdpValue && !loginEvent) {
      // Clean up localStorage sources
      window.localStorage.removeItem(REGISTRATION_QUERY_PARAMS.PARTNER_SSO_REGISTRATION_PARTNER_IDP_QUERY_PARAM)
      window.localStorage.removeItem(REGISTRATION_QUERY_PARAMS.SSO_ENTRY_POINT)
      // Clean up URL params to avoid polluting the address bar
      if (urlPartnerIdp) {
        urlSearchParams.delete(REGISTRATION_QUERY_PARAMS.PARTNER_SSO_REGISTRATION_PARTNER_IDP_QUERY_PARAM)
        urlSearchParams.delete(REGISTRATION_QUERY_PARAMS.SSO_ENTRY_POINT)
        const cleanSearch = urlSearchParams.toString()
        const newUrl = `${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}${window.location.hash}`
        window.history.replaceState(null, '', newUrl)
      }
      setLoginEvent(LOGIN_EVENTS.LOGIN_PARTNER_SSO)
      setLoginEventProps({
        [COMMON_MIXPANEL_PROPERTIES.PARTNER_NAME]: partnerIdpValue,
        ...(ssoEntryPointValue && {
          [COMMON_MIXPANEL_PROPERTIES.SSO_ENTRY_POINT]: ssoEntryPointValue,
        }),
      })
    } else if (localStorageSsoEntryPoint && !localStoragePartnerIdp) {
      // Clean up ssoEntryPoint from localStorage if it was set without partnerIdp (non-redirect registration flow)
      window.localStorage.removeItem(REGISTRATION_QUERY_PARAMS.SSO_ENTRY_POINT)
    }
    if (
      !hasTrackedLogin &&
      !!userId &&
      !!customerId &&
      !!loginEvent &&
      mixpanelInitialized &&
      (!hasPendingHomebaseNavigation || isHomebaseFlagIdentified)
    ) {
      mixpanel.register({
        lyra_id: userId,
        customer_id: customerId,
      })
      mixpanel.alias(userId)
      trackEventWithObj({
        page: COMMON_MIXPANEL_PAGE.LOGIN,
        event: loginEvent,
        ...(hasPendingHomebaseNavigation && is1LyraEnabledForHomebase === true
          ? { options: { transport: 'sendBeacon' } }
          : {}),
        ...loginEventProps,
      })

      setHasTrackedLogin(true)
      const showProviderResultsSurveyUsingDateLastSeen = getShowProviderResultsSurveyUsingDateLastSeen({
        providerResultsSurveyDateLastSeen:
          localStorage.getItem(PROVIDER_RESULTS_SURVEY_DATE_LAST_SEEN_KEY) ?? undefined,
      })
      if (showProviderResultsSurveyUsingDateLastSeen) {
        localStorage.setItem(SHOW_PROVIDER_RESULTS_SURVEY_KEY, 'true')
      } else {
        localStorage.setItem(SHOW_PROVIDER_RESULTS_SURVEY_KEY, 'false')
      }
    }
  }, [
    customerId,
    hasPendingHomebaseNavigation,
    hasTrackedLogin,
    is1LyraEnabledForHomebase,
    isHomebaseFlagIdentified,
    loginEvent,
    loginEventProps,
    mixpanelInitialized,
    trackEventWithObj,
    userId,
  ])

  useEffect(() => {
    if (!hasPendingHomebaseNavigation) {
      return
    }

    if (isHomebaseFlagIdentified) {
      if (is1LyraEnabledForHomebase !== true) {
        setHasPendingHomebaseNavigation(false)
        setIsAuthenticating(false)
        navigate(PAGE_ROUTES.SECURE_INDEX)
        return
      }

      if (hasTrackedLogin) {
        window.location.replace(addStickyQueryParams(PAGE_ROUTES.SECURE_INDEX))
        return
      }
    }

    // LaunchDarkly identify has no browser timeout. Keep an outage from holding
    // an otherwise successful login on the authentication loader indefinitely.
    const timeoutId = window.setTimeout(() => {
      setHasPendingHomebaseNavigation(false)
      setIsAuthenticating(false)
      navigate(PAGE_ROUTES.SECURE_INDEX)
    }, HOMEBASE_NAVIGATION_TIMEOUT_MS)

    return () => window.clearTimeout(timeoutId)
  }, [hasPendingHomebaseNavigation, hasTrackedLogin, is1LyraEnabledForHomebase, isHomebaseFlagIdentified, navigate])

  return isAuthenticating
}

export default useAuthenticate
